import { useState, useCallback } from 'react';
import { VERIFY_MODAL_TABS } from '~/app/constants';

/**
 * useQueueReturn
 *
 * Owns the queue/transmit-return flow state that was previously class state
 * on FormViewer:
 *
 *   queueReturn()                              — post return_xmitlist to socket
 *   handleQueueReturn()                        — set verify type then trigger verify
 *   openQueueReturnModal()                     — open the modal
 *   closeQueueReturnModal()                    — close the modal
 *   closeQueueReturnModalDemo()                — close modal and open demo message
 *   updateTransmitList(list, comments, diag)   — post return_xmitqueue
 *   submitReturnReviewStatus()                 — toggle readyForReview + call calc
 *   setQueueData(transmitItemList, feeItemList, feeTotal, walletBalance, wndwNote)
 *     — batch-set queue data then open modal (called from useSocketMessage)
 *
 * State returned for render:
 *   queueReturnModalOpen
 *   transmitItemList, feeItemList, feeTotal, walletBalance, wndwNote
 *   openDemoMessage
 *   readyForReview
 *
 * Callers must supply:
 *   calcAPIRef        — ref to CalcAPI instance
 *   socketWorkerRef   — ref to socket worker
 *   returnIDRef       — ref to current returnID
 *   verifyRef         — ref to verify hook (for handleQueueReturn)
 *   onSetVerifyType   — callback into class (this.setVerifyType)
 *   initialReadyForReview — initial value from Redux
 */
const useQueueReturn = ({
  calcAPIRef,
  socketWorkerRef,
  returnIDRef,
  verifyRef,
  onSetVerifyType,
  initialReadyForReview,
}) => {
  const [queueReturnModalOpen, setQueueReturnModalOpen] = useState(false);
  const [transmitItemList, setTransmitItemList] = useState({});
  const [feeItemList, setFeeItemList] = useState([]);
  const [feeTotal, setFeeTotal] = useState('');
  const [walletBalance, setWalletBalance] = useState('');
  const [wndwNote, setWndwNote] = useState('');
  const [openDemoMessage, setOpenDemoMessage] = useState(false);
  const [readyForReview, setReadyForReview] = useState(initialReadyForReview ?? false);

  const openQueueReturnModal = useCallback(() => setQueueReturnModalOpen(true), []);
  const closeQueueReturnModal = useCallback(() => setQueueReturnModalOpen(false), []);

  const closeQueueReturnModalDemo = useCallback(() => {
    setQueueReturnModalOpen(false);
    setOpenDemoMessage(true);
  }, []);

  const queueReturn = useCallback(() => {
    socketWorkerRef?.current?.postMessage(
      JSON.stringify({
        command: 'return_xmitlist',
        returnID: parseInt(returnIDRef.current),
      }),
    );
  }, [socketWorkerRef, returnIDRef]);

  const handleQueueReturn = useCallback(() => {
    onSetVerifyType('queueReturn');
    verifyRef.current?.verifyReturn(VERIFY_MODAL_TABS.VERIFY);
  }, [onSetVerifyType, verifyRef]);

  const updateTransmitList = useCallback(
    (list, comments, isDiagnosticRequest) => {
      calcAPIRef.current?.SetReturnReviewStatus(!readyForReview);
      socketWorkerRef?.current?.postMessage(
        JSON.stringify({
          command: 'return_xmitqueue',
          returnID: parseInt(returnIDRef.current),
          XmitListItems: list,
          comments,
          suspended: isDiagnosticRequest,
        }),
      );
    },
    [calcAPIRef, socketWorkerRef, returnIDRef, readyForReview],
  );

  const submitReturnReviewStatus = useCallback(() => {
    calcAPIRef.current?.SetReturnReviewStatus(!readyForReview);
    setReadyForReview(v => !v);
  }, [calcAPIRef, readyForReview]);

  // Called from useSocketMessage to batch-set queue data and open the modal
  const setQueueData = useCallback(data => {
    setTransmitItemList(data.transmitItemList);
    setFeeItemList(data.feeItemList);
    setFeeTotal(data.feeTotal);
    setWalletBalance(data.walletBalance);
    setWndwNote(data.wndwNote);
    setQueueReturnModalOpen(true);
  }, []);

  return {
    queueReturnModalOpen,
    transmitItemList,
    feeItemList,
    feeTotal,
    walletBalance,
    wndwNote,
    openDemoMessage,
    setOpenDemoMessage,
    readyForReview,
    setReadyForReview,
    queueReturn,
    handleQueueReturn,
    openQueueReturnModal,
    closeQueueReturnModal,
    closeQueueReturnModalDemo,
    updateTransmitList,
    submitReturnReviewStatus,
    setQueueData,
  };
};

export default useQueueReturn;
